import { FC } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';

import { IconButtonRounded, NgErrorMessage } from '../UI';
import { addNotification } from '../../store';
import { useAssetDownload, useUpload } from '../../hooks';
import { NgFileUpload } from '../NgFileUpload/NgFileUpload';

const StyledFileDownload = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};

  a {
    &:hover {
      text-decoration: underline;
    }
  }
`;

interface Props {
  asset: any;
  uploadFolderId: number;
  isLoading?: boolean;
  acceptedFileTypes: string;
  helperText?: string;
  uploadTitle?: string;
  downloadTitle?: string;
  onUpload?: (data: any) => void;
}

export const NgAssetUploadInput: FC<Props> = ({
  asset,
  uploadFolderId,
  acceptedFileTypes,
  uploadTitle,
  downloadTitle = 'Download Asset',
  helperText,
  onUpload,
  isLoading: isPArentLoading,
}) => {
  const dispatch = useDispatch();
  const { uploadFiles } = useUpload();
  const { downloadAssetById } = useAssetDownload();

  const handleUploadFile = async (file: any) => {
    try {
      const response = await uploadFiles({ file: file, folderId: uploadFolderId });

      if (typeof onUpload === 'function') {
        onUpload(response);
      }
    } catch (error: any) {
      console.error('Error uploading file');
    }
  };

  const isLoading = isPArentLoading;

  const handleDownloadFile = async (e: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!asset?.ID) {
      const message = 'Could not download template file: No asset provided!';
      dispatch(
        addNotification({
          type: 'error',
          message: message,
        }),
      );

      return;
    }

    downloadAssetById({ assetId: asset.ID });
  };

  return (
    <>
      {asset && (
        <StyledFileDownload onClick={handleDownloadFile}>
          <a href="#">{asset?.DisplayName}</a>
          <IconButtonRounded title={downloadTitle} icon={<CloudDownloadOutlinedIcon />} filled />
        </StyledFileDownload>
      )}

      {
        helperText && <small>{helperText}</small>
      }

      {uploadFolderId ? (
        <NgFileUpload
          isLoading={isLoading}
          title={uploadTitle}
          onUpload={handleUploadFile}
          targetId={uploadFolderId}
          acceptedFileTypes={acceptedFileTypes}
        />
      ) : (
        <NgErrorMessage message="Error: no upload folder ID provided!" logError />
      )}
    </>
  );
};
